import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  fetchRetentionPipeline,
  fetchRetentionPipelineMonths,
  RetentionPipelineData,
  PmRetentionRow,
} from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import GlassSelect from '../components/GlassSelect';
import ScaleToFit from '../components/ScaleToFit'; // [RESPONSIVE 2026-06-23]
import Tooltip from '../components/Tooltip'; // [RETENTION-TOOLTIPS 2026-09-22]
import { SkeletonBox, TableSkeleton, Reveal } from '../components/Skeleton';
import { LoadFailed } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { effectiveCurrentMonth } from '../utils/effectiveMonth';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-22] phones get the pipeline as a list
import { lgTile, lgSquircle } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]

// [RETENTION-TOOLTIPS 2026-09-22] One-line hover explanation per pipeline box, worded from
// computePipeline in server/src/services/retentionPipelineCache.ts. Kept short on purpose: the
// shared Tooltip is single-line and centred under its box without edge clamping, so the leftmost
// node and the right-hand capsules must stay narrow enough not to run off-screen.
const TIPS = {
  somInventory: 'Total inventory for the month — every type, not just Recurring',
  exclusions: 'One-Time inventory — not counted in the pipeline below',
  initialExpected: "This month's Recurring inventory — the base for retention %",
  currentExpected: 'Expected rows still to collect: Pending + Awaited',
  expectedPending: 'Expected rows, no payment yet — full amount',
  expectedAwaited: 'Expected rows part-paid — the balance still due',
  atRisk: 'Full amount of At Risk rows (not a sum of the two below)',
  atRiskExpected: 'At Risk Expected rows — expected payment, else full amount',
  atRiskAwaited: 'At Risk + At Risk Expected rows with some retention — balance due',
  retained: "Retention already received on this month's Recurring rows",
  cancelled: 'Full amount of rows marked Cancelled',
  discount: "Total discount on this month's Recurring rows",
  expectedEOM: 'Expected EOM Retention + New Sales − PM refunds/disputes',
  expectedEOMRetention: 'Retained + Current Expected + At Risk Expected & Awaited',
};


/* [PERF 2026-08-14] Built ONCE - `toLocaleDateString(locale, {options})` constructs a fresh
 * Intl.DateTimeFormat per call (measured 90us on V8 vs 1.5us cached). Output identical. */
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

// [RESPONSIVE 2026-06-23] Natural minimum width of the whole flowchart layout:
// the tree's branch row is 1336px (5 branches: 356+356+160+160+160 + 4×36 gaps),
// plus the 32px column gap and the 380px capsule column = 1748px. Below this the
// ScaleToFit wrapper shrinks the entire block uniformly so it stays fully visible
// (and looks identical, just smaller) instead of clipping / colliding.
const FC_LAYOUT_MIN_WIDTH = 1748;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-width: 0;
  background: ${(p) => p.theme.surface};
  /* Clear the floating bottom dock so the last table row (the Total) sits above
     the pill instead of hiding behind it. */
  padding-bottom: 88px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* [MOBILE 2026-07-07] Keep the title + month picker on ONE row (title left,
     filter right) on phones instead of stacking them. Retention has only those
     two controls, so the single-row layout reads cleaner and matches Stats — the
     base's row direction + space-between already do it, so we just tighten the
     padding here and let the natural-width filter sit on the right. The base
     flex-wrap stays as a safety net for an extreme-narrow width.
     [PHONE-LAYOUT 2026-10-26, user] Phones: the head is centred like every other tab now — the title, then the month
     box under it (the user's 09-23 rule for page heads; Stats left this one-row look the same way on 09-23). */
  @media (max-width: 768px) {
    padding: 10px 12px;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  /* [PHONE-LAYOUT 2026-10-26] Phones: the page-head title of the other tabs, centred. */
  @media (max-width: 768px) {
    font-size: 17px;
    font-weight: 800;
    text-align: center;
  }
`;

const FilterSelect = GlassSelect;

const Section = styled.section`
  padding: 20px;
  /* [PHONE-LAYOUT 2026-10-22] Phones: the 20 px gutter becomes 14 / 12 px, so the cards get the width. */
  @media (max-width: 768px) {
    padding: 14px 12px;
  }
`;

const SectionHeading = styled.h2`
  grid-column: 1;
  grid-row: 1;
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  margin: 0 0 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 3px;
    height: 16px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    border-radius: 2px;
  }
`;

const FlowchartFrame = styled.div`
  position: relative;
  isolation: isolate;
  /* Background is near-opaque and sits on the solid page card, so a backdrop
     blur added nothing visible — but a heavy backdrop-filter here flashed a
     white patch while AppLayout's keyed PageFade (opacity + translateY) animates
     in (a Chromium backdrop-filter repaint glitch under an animating/transformed
     ancestor). Dropping the filter removes the flicker with no visual change. */
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(65, 65, 65, 0.96) 0%, rgba(45, 45, 45, 0.97) 100%)'
    : 'rgba(229, 232, 231, 0.98)'};
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(22,25,23,0.10)'};
  border-radius: 20px;
  padding: 24px;
  /* [RESPONSIVE 2026-06-23] The ScaleToFit wrapper inside owns horizontal fitting
     and scrolling now, so the frame just clips to its rounded corners. */
  overflow: hidden;
  box-shadow:
    0 30px 80px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.18)'},
    0 8px 24px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(0,0,0,0.10)'},
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)'},
    inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)'};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.12) 100%);
    pointer-events: none;
    z-index: 0;
  }
  & > * { position: relative; z-index: 1; }
  /* [PHONE-LAYOUT 2026-10-22] Phones: a tighter frame around the pipeline list. */
  @media (max-width: 768px) {
    padding: 14px 14px 12px;
    border-radius: 16px;
    &::before { border-radius: 16px; }
  }
`;

const FcLayout = styled.div`
  /* Heading + tree stack in column 1 (rows 1 & 2); the capsules sit in column 2
     and span BOTH rows so they vertically center against the full box height
     (heading + tree), not just the tree — equal space above and below. */
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 32px;
  align-items: start;
`;

const FcTree = styled.div`
  grid-column: 1;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* [RESPONSIVE 2026-06-23] Was 960px — but an explicit min-width REPLACES the
     grid item's content-based minimum, letting the column shrink to 960 while the
     branch row stayed 1336px and spilled out both sides (left node clipped, right
     node colliding with the capsule). Pin it to the true content width so the tree
     never self-clips; ScaleToFit then scales the whole block to fit the screen. */
  min-width: 1336px;
`;

const FcCapsules = styled.div`
  grid-column: 2;
  grid-row: 1 / 3;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 380px;
  max-width: 100%;
`;

const Capsule = styled.div`
  position: relative;
  isolation: isolate;
  /* [LIQUID-GLASS 2026-11-02] it was already glossy; now a shine on its top half too */
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0) 50%), linear-gradient(180deg, rgba(86, 86, 86, 0.48) 0%, rgba(54, 54, 54, 0.60) 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0) 55%), rgba(241, 245, 249, 0.92)'};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch); the capsule sits on the opaque flowchart frame. */
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(22,25,23,0.10)'};
  border-radius: 999px;
  width: 100%;
  min-height: 96px;
  padding: 22px 32px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  box-shadow:
    0 8px 24px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.10)'},
    0 2px 6px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.06)'},
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.60)'},
    inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.03)'};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(0,0,0,0) 78%, rgba(0,0,0,0.10) 100%);
    pointer-events: none;
    z-index: 0;
  }
  & > * { position: relative; z-index: 1; }
`;

const CapsuleAccent = styled.span`
  position: relative;
  z-index: 1;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600};
  font-weight: 700;
  ${(p) => p.theme.colorScheme === 'dark' && 'text-shadow: 0 0 8px rgba(74, 205, 139, 0.45);'}
`;

const CapsuleLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
`;

const CapsuleValue = styled.div`
  font-size: 22px;
  font-weight: 700;
`;

const FcNode = styled.div<{ $merge?: boolean }>`
  position: relative;
  isolation: isolate;
  /* Flat translucent glass — no top-to-bottom gradient so the small node
     box doesn't show a lighter band where labels sit. */
  /* [LIQUID-GLASS 2026-11-02] a shine on its top half and a light top edge (below) */
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0) 50%), rgba(54, 54, 54, 0.55)'
    : 'linear-gradient(180deg, #ffffff, rgba(255,255,255,0) 55%), rgba(255, 255, 255, 0.96)'};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch); the node sits on the opaque flowchart frame. */
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(22,25,23,0.14)'};
  border-radius: 10px;
  padding: 10px 14px;
  min-width: 160px;
  text-align: center;
  box-shadow:
    0 6px 16px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(0,0,0,0.08)'},
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.9)'},
    inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.04)'};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 10px;
    background: linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.10) 100%);
    pointer-events: none;
    z-index: 0;
  }
  & > * { position: relative; z-index: 1; }
`;

const NodeLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  margin-bottom: 4px;
`;

const NodeValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

/* Vertical connector — sits between two stacked nodes */
const VConn = styled.div`
  width: 2px;
  height: 22px;
  background: ${(p) => p.theme.text};
`;

/* Top row: the "SOM Inventory" node sits to the LEFT of the (centered)
   "Exclusions" node, joined by a short horizontal connector. Exclusions stays
   centered so the vertical line dropping to "Initial Expected" lines up with the
   rest of the tree; SOM Inventory hangs off to the side (absolutely positioned)
   without shifting that centre. */
const ExclRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
`;

/* Fixed 160px width on both top nodes so the connector maths below is exact. */
const TopNode = styled(FcNode)`
  width: 160px;
`;

const SomNode = styled(TopNode)`
  position: absolute;
  top: 0;
  /* right edge meets the connector's left end (Exclusions' left edge is 50%−80px;
     the 44px connector reaches 50%−124px). */
  right: calc(50% + 124px);
`;

const SomConn = styled.div`
  position: absolute;
  top: 50%;
  right: calc(50% + 80px); /* starts at the centered Exclusions node's left edge */
  width: 44px;             /* and runs left to meet SOM Inventory */
  height: 2px;
  background: ${(p) => p.theme.text};
  transform: translateY(-1px);
`;

/* Row of branches — children spaced apart, horizontal connector lines drawn via
   each Branch's ::after segment */
const BranchRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 36px;
`;

/* Individual branch — has a vertical line above (::before) connecting to parent's
   horizontal bar, and a horizontal segment (::after) joining adjacent branches */
const Branch = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 22px;

  /* Vertical drop from horizontal bar into this branch's top node */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 2px;
    height: 22px;
    background: ${(p) => p.theme.text};
    transform: translateX(-1px);
  }

  /* Horizontal segment at top connecting to siblings — extends half the gap (18px)
     into each side so adjacent siblings' segments meet without gaps. */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    height: 2px;
    background: ${(p) => p.theme.text};
    left: -18px;
    right: -18px;
  }

  &:first-child::after { left: 50%; right: -18px; }
  &:last-child::after  { left: -18px; right: 50%; }
  &:only-child::after  { display: none; }
`;

/* [LIQUID-GLASS 2026-11-02, user] "make this table same just like it as before": the PM Retention Analysis table keeps its old
   frame (no glass) */
const TableWrapper = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 8px;
  overflow: auto;
  /* [PHONE-LAYOUT 2026-10-22] Phones: the sideways swipe stays inside the table (never the browser's back gesture). */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 1100px;
  /* [PHONE-LAYOUT 2026-10-22] Phones: columns take their natural width (no squeeze to 1100 px), 13 px figures. */
  @media (max-width: 768px) {
    width: max-content;
    min-width: 100%;
    font-size: 13px;
  }
`;

const Th = styled.th`
  background: ${(p) => p.theme.pill};
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  &:first-child { text-align: left; }
  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px headers (10 px was below the phone minimum), the app's 8 / 10 px cells. */
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 8px 10px;
  }
`;

const Td = styled.td`
  padding: 8px 12px;
  text-align: center;
  color: ${(p) => p.theme.text};
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  &:first-child { text-align: left; font-weight: 600; }
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    padding: 8px 10px;
  }
`;

/* [PHONE-LAYOUT 2026-10-22] Phones: the Retention Pipeline as a top-down list — the same numbers in the tree's order
   (SOM Inventory and Exclusions above the root, Initial Expected, its five branches with the two sub-branches indented,
   then the two capsules). The 1,748 px tree showed only its left half on a phone, at half size, with 10 px labels.
   Rendered only when useIsMobile() is true; the laptop keeps the tree. */
const PhonePipe = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2px;
`;

const PhoneRow = styled.div<{ $depth?: number; $strong?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0 8px ${(p) => (p.$depth || 0) * 16}px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  font-size: ${(p) => (p.$strong ? '14px' : '13px')};
  & > span:first-child {
    min-width: 0;
    font-weight: ${(p) => (p.$strong ? 700 : 600)};
    color: ${(p) => (p.$strong ? p.theme.text : p.theme.muted)};
  }
  & > span:last-child {
    flex-shrink: 0;
    font-weight: 700;
    color: ${(p) => p.theme.text};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

const PhoneCaps = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
`;

const PhoneCap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  ${lgSquircle(12)}
  ${lgTile} /* [LIQUID-GLASS 2026-11-02] glass cards */
  & > span:first-child {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${(p) => p.theme.muted};
  }
  & > span:nth-child(2) { font-size: 15px; font-weight: 800; overflow-wrap: anywhere; }
  & > span:nth-child(3) { font-size: 12px; font-weight: 600; color: ${(p) => p.theme.muted}; }
`;

/* Data row with the same subtle hover tint as the shared DataTable, so this
   custom table reads consistently with the rest of the app. The tint is painted
   on the cells and guarded by hover:hover so touch devices never get a sticky
   highlight. The html.pointer-active prefix (set by DataTable on the first real
   pointer move) keeps a hard refresh with the cursor parked on a row from
   painting — and sticking — the highlight on load. */
const BodyRow = styled.tr`
  & > td {
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  @media (hover: hover) {
    html.pointer-active &:hover > td {
      background: ${(p) =>
        p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.04)'};
    }
  }
`;

const TotalRow = styled.tr`
  background: ${(p) => p.theme.pill};
  td { font-weight: 700; border-bottom: none; }
`;

function fmtMoney(n: number): string {
  return '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtPct(n: number): string {
  return (n || 0).toFixed(1) + '%';
}

// [MONTH-FULL-NAME] 2026-06-23 — full month name (e.g. "June 2026") to match the
// EOM page. Used only by the month filter dropdown.
function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return MONTH_LONG_YEAR_FMT.format(new Date(parseInt(y, 10), parseInt(m, 10) - 1));
}

// [EFFECTIVE-MONTH 2026-07-01] App-wide effective current month (12:30 PM IST
// rollover on the 1st) so the default month lines up with the rest of the app.
function getCurrentYm(): string {
  return effectiveCurrentMonth();
}

const RetentionAnalysisPage: React.FC = () => {
  const theme = useTheme() as any;
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22]
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState<string>(getCurrentYm());
  const [data, setData] = useState<RetentionPipelineData | null>(null);
  const [loading, setLoading] = useState(true);
  // [LOAD-STATES 2026-09-29, user] A failed load left `data` null, and "loading || !data" kept the
  // skeleton up FOR EVER. Now it shows the "Couldn't load retention analysis" countdown card.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    fetchRetentionPipelineMonths()
      .then((ms) => {
        // Default to (and stay on) the current month even if its data hasn't
        // landed yet — don't snap back to the most recent month with data. The
        // current month is still selectable via the dropdown (added below).
        setMonths(ms);
      })
      .catch((err) => console.error('Failed to load months:', err));
  }, []);

  useEffect(() => {
    // Everything (tree, capsules, PM table) now comes from the single retention
    // pipeline call — no separate in-bank fetch needed.
    let alive = true;
    setLoading(true);

    fetchRetentionPipeline(month)
      .then((d) => { if (alive) { setData(d); setLoadFailed(false); } })
      // [LOAD-STATES 2026-09-29] Drop the previous month's figures too: they are not this month's.
      .catch((err) => { console.error('Failed to load retention pipeline:', err); if (alive) { setData(null); setLoadFailed(true); } })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [month]);

  // [LOAD-STATES 2026-09-29] The countdown card's retry.
  const retry = () => fetchRetentionPipeline(month).then((d) => { setData(d); setLoadFailed(false); }).catch(() => {});

  // [REALTIME] 2026-06-23 — when the server signals the retention data changed
  // (from ANY source), silently refetch the current month. No spinner/flicker.
  useRealtime(['retention'], () => {
    fetchRetentionPipeline(month).then(setData).catch(() => {});
  });

  // PM table totals — sum each tree-node column across PMs; the % rows recompute
  // from the summed bases so the total row's % is consistent with its own figures.
  const totals = useMemo(() => {
    if (!data) return null;
    const t = {
      initialExpected: 0, currentExpected: 0, expectedPending: 0, expectedAwaited: 0,
      atRisk: 0, atRiskExpected: 0, atRiskAwaited: 0, retained: 0, cancelled: 0,
      discount: 0, expectedEOMRetention: 0,
    };
    for (const r of data.pmRetention) {
      t.initialExpected += r.initialExpected;
      t.currentExpected += r.currentExpected;
      t.expectedPending += r.expectedPending;
      t.expectedAwaited += r.expectedAwaited;
      t.atRisk += r.atRisk;
      t.atRiskExpected += r.atRiskExpected;
      t.atRiskAwaited += r.atRiskAwaited;
      t.retained += r.retained;
      t.cancelled += r.cancelled;
      t.discount += r.discount;
      t.expectedEOMRetention += r.expectedEOMRetention;
    }
    const currentPct = t.initialExpected > 0 ? (t.retained / t.initialExpected) * 100 : 0;
    const expectedPct = t.initialExpected > 0 ? (t.expectedEOMRetention / t.initialExpected) * 100 : 0;
    return { ...t, currentPct, expectedPct };
  }, [data]);

  return (
    <Container>
      <Toolbar>
        <PageTitle>Retention Analysis</PageTitle>
        <FilterSelect liquid /* [LIQUID-GLASS 2026-11-02] */ value={month} onChange={(e) => setMonth(e.target.value)}>
          {months.length === 0 && <option value={month}>{formatMonthLabel(month)}</option>}
          {(months.length > 0 && !months.includes(month) ? [month, ...months] : months).map((m) => (
            <option key={m} value={m}>{formatMonthLabel(m)}</option>
          ))}
        </FilterSelect>
      </Toolbar>

      {!loading && loadFailed && !data ? (
        <LoadFailed what="retention analysis" onRetry={retry} />
      ) : loading || !data ? (
        // Mirror the real two-section layout: the bordered flowchart frame (heading
        // + tree area) AND the PM Retention table below — not a single block.
        <>
          <Section>
            <FlowchartFrame>
              <SectionHeading><SkeletonBox $w="160px" $h="13px" /></SectionHeading>
              {/* [PHONE-LAYOUT 2026-10-22] phones: the height of the pipeline list, so nothing jumps when it lands */}
              <SkeletonBox $h={isMobile ? '466px' : '320px'} $radius="12px" $mt="16px" />
            </FlowchartFrame>
          </Section>
          <Section>
            <SectionHeading><SkeletonBox $w="180px" $h="13px" /></SectionHeading>
            <TableWrapper>
              <TableSkeleton
                rows={4}
                columns={[
                  'PM Name', 'Initial Expected', 'Current Expected', 'Expected Pending',
                  'Expected Awaited', 'At Risk', 'At Risk Expected', 'At Risk Awaited',
                  'Retained', 'Cancelled', 'Discount', 'Expected EOM Retention',
                  'Current Retention %', 'Expected Retention %',
                ].map((label) => ({ label }))}
              />
            </TableWrapper>
          </Section>
        </>
      ) : (
        <Reveal $plain $rows /* [FLUID-4 2026-10-12] first rows rise in as the data lands */>
          {/* [MOTION-A 2026-08-20] Fades the real content in as it replaces the skeleton. */}
        <>
          <Section>
            <FlowchartFrame>
             {/* [PHONE-LAYOUT 2026-10-22] Phones: the same numbers as a top-down list (see PhonePipe). */}
             {isMobile ? (
              <>
                <SectionHeading>Retention Pipeline</SectionHeading>
                <PhonePipe>
                  <PhoneRow><span>SOM Inventory</span><span>{fmtMoney(data.flowchart.somInventory)}</span></PhoneRow>
                  <PhoneRow><span>Exclusions</span><span>{fmtMoney(data.flowchart.exclusions)}</span></PhoneRow>
                  <PhoneRow $strong><span>Initial Expected</span><span>{fmtMoney(data.flowchart.initialExpected)}</span></PhoneRow>
                  <PhoneRow $depth={1}><span>Current Expected</span><span>{fmtMoney(data.flowchart.nodeExpectedPending + data.flowchart.nodeExpectedAwaited)}</span></PhoneRow>
                  <PhoneRow $depth={2}><span>Expected Pending</span><span>{fmtMoney(data.flowchart.nodeExpectedPending)}</span></PhoneRow>
                  <PhoneRow $depth={2}><span>Expected Awaited</span><span>{fmtMoney(data.flowchart.nodeExpectedAwaited)}</span></PhoneRow>
                  <PhoneRow $depth={1}><span>At Risk</span><span>{fmtMoney(data.flowchart.nodeAtRisk)}</span></PhoneRow>
                  <PhoneRow $depth={2}><span>At Risk Expected</span><span>{fmtMoney(data.flowchart.nodeAtRiskExpected)}</span></PhoneRow>
                  <PhoneRow $depth={2}><span>At Risk Awaited</span><span>{fmtMoney(data.flowchart.nodeAtRiskAwaited)}</span></PhoneRow>
                  <PhoneRow $depth={1}><span>Retained</span><span>{fmtMoney(data.flowchart.retained)}</span></PhoneRow>
                  <PhoneRow $depth={1}><span>Cancelled</span><span>{fmtMoney(data.flowchart.cancelled)}</span></PhoneRow>
                  <PhoneRow $depth={1}><span>Discount</span><span>{fmtMoney(data.flowchart.discountTotal)}</span></PhoneRow>
                </PhonePipe>
                <PhoneCaps>
                  <PhoneCap>
                    <span>Expected EOM</span>
                    <CapsuleAccent>{fmtMoney(data.expectedEOM)}</CapsuleAccent>
                  </PhoneCap>
                  <PhoneCap>
                    <span>Expected EOM Retention</span>
                    <CapsuleAccent>{fmtMoney(data.expectedEOMRetention)}</CapsuleAccent>
                    <span>({fmtPct(data.expectedEOMRetentionPct)})</span>
                  </PhoneCap>
                </PhoneCaps>
              </>
             ) : (
             /* [RESPONSIVE 2026-06-23] Scale the whole pipeline down to fit any
                 screen so it stays fully visible (no cut nodes, no capsule overlap). */
             <ScaleToFit contentMinWidth={FC_LAYOUT_MIN_WIDTH} minScale={0.5}>
              <FcLayout>
                <SectionHeading>Retention Pipeline</SectionHeading>
                <FcTree>
                  {/* Exclusions (One-Time inventory) with SOM Inventory (the
                      month's total inventory) joined to its left. Both sit above
                      the pipeline's root, Initial Expected. */}
                  <ExclRow>
                    <SomConn />
                    {/* [RETENTION-TOOLTIPS 2026-09-22] every box below is wrapped in a Tooltip */}
                    <Tooltip label={TIPS.somInventory}>
                      <SomNode>
                        <NodeLabel>SOM Inventory</NodeLabel>
                        <NodeValue>{fmtMoney(data.flowchart.somInventory)}</NodeValue>
                      </SomNode>
                    </Tooltip>
                    <Tooltip label={TIPS.exclusions}>
                      <TopNode>
                        <NodeLabel>Exclusions</NodeLabel>
                        <NodeValue>{fmtMoney(data.flowchart.exclusions)}</NodeValue>
                      </TopNode>
                    </Tooltip>
                  </ExclRow>
                  <VConn />
                  {/* Root */}
                  <Tooltip label={TIPS.initialExpected}>
                    <FcNode>
                      <NodeLabel>Initial Expected</NodeLabel>
                      <NodeValue>{fmtMoney(data.flowchart.initialExpected)}</NodeValue>
                    </FcNode>
                  </Tooltip>
                  <VConn />
                  <BranchRow>
                    {/* Current Expected branch → Expected Pending + Expected Awaited.
                        Both leaves are computed directly from the inventory tab
                        (Recurring rows, selected month):
                          Expected Pending = status Expected & Received = 0 → SUM(Amount)
                          Expected Awaited = status Expected & Received > 0 → SUM(Pending Payment)
                        Current Expected = the two summed. */}
                    <Branch>
                      <Tooltip label={TIPS.currentExpected}>
                        <FcNode>
                          <NodeLabel>Current Expected</NodeLabel>
                          <NodeValue>{fmtMoney(data.flowchart.nodeExpectedPending + data.flowchart.nodeExpectedAwaited)}</NodeValue>
                        </FcNode>
                      </Tooltip>
                      <VConn />
                      <BranchRow>
                        <Branch>
                          <Tooltip label={TIPS.expectedPending}>
                            <FcNode>
                              <NodeLabel>Expected Pending</NodeLabel>
                              <NodeValue>{fmtMoney(data.flowchart.nodeExpectedPending)}</NodeValue>
                            </FcNode>
                          </Tooltip>
                        </Branch>
                        <Branch>
                          <Tooltip label={TIPS.expectedAwaited}>
                            <FcNode>
                              <NodeLabel>Expected Awaited</NodeLabel>
                              <NodeValue>{fmtMoney(data.flowchart.nodeExpectedAwaited)}</NodeValue>
                            </FcNode>
                          </Tooltip>
                        </Branch>
                      </BranchRow>
                    </Branch>

                    {/* [RETENTION-NODES 2026-06-23]
                        At Risk branch → At Risk Expected + At Risk Awaited, all
                        from the inventory tab (Recurring rows, selected month):
                          At Risk          = status At - Risk → SUM(Amount)
                          At Risk Expected = status At - Risk Expected → SUM(At-Risk Expected Payment)
                          At Risk Awaited  = status At-Risk OR At-Risk-Expected, Retention≠0 & Retention<Amount → SUM(Amount − Retention)
                        (the parent At Risk is its own inventory figure, not the
                        sum of the two children.) */}
                    <Branch>
                      <Tooltip label={TIPS.atRisk}>
                        <FcNode>
                          <NodeLabel>At Risk</NodeLabel>
                          <NodeValue>{fmtMoney(data.flowchart.nodeAtRisk)}</NodeValue>
                        </FcNode>
                      </Tooltip>
                      <VConn />
                      <BranchRow>
                        <Branch>
                          <Tooltip label={TIPS.atRiskExpected}>
                            <FcNode>
                              <NodeLabel>At Risk Expected</NodeLabel>
                              <NodeValue>{fmtMoney(data.flowchart.nodeAtRiskExpected)}</NodeValue>
                            </FcNode>
                          </Tooltip>
                        </Branch>
                        <Branch>
                          <Tooltip label={TIPS.atRiskAwaited}>
                            <FcNode>
                              <NodeLabel>At Risk Awaited</NodeLabel>
                              <NodeValue>{fmtMoney(data.flowchart.nodeAtRiskAwaited)}</NodeValue>
                            </FcNode>
                          </Tooltip>
                        </Branch>
                      </BranchRow>
                    </Branch>

                    {/* Retained */}
                    <Branch>
                      <Tooltip label={TIPS.retained}>
                        <FcNode>
                          <NodeLabel>Retained</NodeLabel>
                          <NodeValue>{fmtMoney(data.flowchart.retained)}</NodeValue>
                        </FcNode>
                      </Tooltip>
                    </Branch>

                    {/* Cancelled */}
                    <Branch>
                      <Tooltip label={TIPS.cancelled}>
                        <FcNode>
                          <NodeLabel>Cancelled</NodeLabel>
                          <NodeValue>{fmtMoney(data.flowchart.cancelled)}</NodeValue>
                        </FcNode>
                      </Tooltip>
                    </Branch>

                    {/* Discount — from the inventory tab (Recurring rows, selected
                        month, any status) → SUM(Discount). */}
                    <Branch>
                      <Tooltip label={TIPS.discount}>
                        <FcNode>
                          <NodeLabel>Discount</NodeLabel>
                          <NodeValue>{fmtMoney(data.flowchart.discountTotal)}</NodeValue>
                        </FcNode>
                      </Tooltip>
                    </Branch>
                  </BranchRow>
                </FcTree>

                <FcCapsules>
                  {/* [RETENTION-NODES 2026-06-23]
                      Expected EOM = Retained + Expected Pending + Expected Awaited
                      + At Risk Expected + At Risk Awaited (the five tree nodes)
                      + New Sales (Recurring + One-Time, from the Payments In-Bank
                      Summary) − refund/dispute(FALSE, PM). */}
                  <Tooltip label={TIPS.expectedEOM}>
                    <Capsule>
                      <CapsuleLabel>Expected EOM</CapsuleLabel>
                      <CapsuleValue>
                        <CapsuleAccent>{fmtMoney(data.expectedEOM)}</CapsuleAccent>
                      </CapsuleValue>
                    </Capsule>
                  </Tooltip>
                  {/* [RETENTION-NODES 2026-06-23]
                      Expected EOM Retention = Retained + Expected Pending + Expected Awaited
                      + At Risk Expected + At Risk Awaited (the five tree nodes only). */}
                  <Tooltip label={TIPS.expectedEOMRetention}>
                    <Capsule>
                      <CapsuleLabel>Expected EOM Retention</CapsuleLabel>
                      <CapsuleValue>
                        <CapsuleAccent>{fmtMoney(data.expectedEOMRetention)}</CapsuleAccent>{' '}
                        <span style={{ color: theme.muted }}>({fmtPct(data.expectedEOMRetentionPct)})</span>
                      </CapsuleValue>
                    </Capsule>
                  </Tooltip>
                </FcCapsules>
              </FcLayout>
             </ScaleToFit>
             )}
            </FlowchartFrame>
          </Section>

          <Section>
            <SectionHeading>PM Retention Analysis</SectionHeading>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>PM Name</Th>
                    <Th>Initial Expected</Th>
                    <Th>Current Expected</Th>
                    <Th>Expected Pending</Th>
                    <Th>Expected Awaited</Th>
                    <Th>At Risk</Th>
                    <Th>At Risk Expected</Th>
                    <Th>At Risk Awaited</Th>
                    <Th>Retained</Th>
                    <Th>Cancelled</Th>
                    <Th>Discount</Th>
                    <Th>Expected EOM Retention</Th>
                    <Th>Current Retention %</Th>
                    <Th>Expected Retention %</Th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.pmRetention]
                    .sort((a, b) => (a.pmName || '').localeCompare(b.pmName || ''))
                    .map((r: PmRetentionRow) => (
                    <BodyRow key={r.pmName}>
                      <Td>{r.pmName}</Td>
                      <Td>{fmtMoney(r.initialExpected)}</Td>
                      <Td>{fmtMoney(r.currentExpected)}</Td>
                      <Td>{fmtMoney(r.expectedPending)}</Td>
                      <Td>{fmtMoney(r.expectedAwaited)}</Td>
                      <Td>{fmtMoney(r.atRisk)}</Td>
                      <Td>{fmtMoney(r.atRiskExpected)}</Td>
                      <Td>{fmtMoney(r.atRiskAwaited)}</Td>
                      <Td>{fmtMoney(r.retained)}</Td>
                      <Td>{fmtMoney(r.cancelled)}</Td>
                      <Td>{fmtMoney(r.discount)}</Td>
                      <Td>{fmtMoney(r.expectedEOMRetention)}</Td>
                      <Td>{fmtPct(r.currentRetentionPct)}</Td>
                      <Td>{fmtPct(r.expectedRetentionPct)}</Td>
                    </BodyRow>
                  ))}
                  {totals && (
                    <TotalRow>
                      <Td>Total</Td>
                      <Td>{fmtMoney(totals.initialExpected)}</Td>
                      <Td>{fmtMoney(totals.currentExpected)}</Td>
                      <Td>{fmtMoney(totals.expectedPending)}</Td>
                      <Td>{fmtMoney(totals.expectedAwaited)}</Td>
                      <Td>{fmtMoney(totals.atRisk)}</Td>
                      <Td>{fmtMoney(totals.atRiskExpected)}</Td>
                      <Td>{fmtMoney(totals.atRiskAwaited)}</Td>
                      <Td>{fmtMoney(totals.retained)}</Td>
                      <Td>{fmtMoney(totals.cancelled)}</Td>
                      <Td>{fmtMoney(totals.discount)}</Td>
                      <Td>{fmtMoney(totals.expectedEOMRetention)}</Td>
                      <Td>{fmtPct(totals.currentPct)}</Td>
                      <Td>{fmtPct(totals.expectedPct)}</Td>
                    </TotalRow>
                  )}
                </tbody>
              </Table>
            </TableWrapper>
          </Section>
        </>
        </Reveal>
      )}
    </Container>
  );
};

export default RetentionAnalysisPage;
